import { createPortal } from "react-dom";
import { useEffect } from "react";
import { CalendarDays, Info, X } from "lucide-react";
import { EditDatePicker } from "@/components/ui/EditDatePicker";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";
import modalStyles from "../../../styles/modals.module.css";
import type { JobEndDatePopupProps } from "../../../types/tabs";

export function JobEndDatePopup({
  isOpen,
  value,
  onChange,
  onSave,
  onCancel,
}: JobEndDatePopupProps) {
  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      onCancel();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  const isValid = !!value;

  const content = (
    <div
      className={`${modalStyles.modalOverlay} !z-[2147483640]`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="job-end-date-title"
      onClick={onCancel}
    >
      <div
        className={`${modalStyles.modalContent} ${modalStyles.jobEndDateModal}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={modalStyles.jobEndDateModalHeader}>
          <h3 id="job-end-date-title" className={modalStyles.jobEndDateModalTitle}>
            <span className={modalStyles.jobEndDateModalTitleIcon} aria-hidden="true">
              <CalendarDays className="h-[18px] w-[18px]" />
            </span>
            Job End Date
          </h3>
          <button
            type="button"
            data-testid="application-income-tab-job-end-date-close-button"
            onClick={onCancel}
            className={modalStyles.closeButton}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className={modalStyles.jobEndDateModalBody}>
          <div className={modalStyles.jobEndDateCallout} role="note">
            <Info className={`h-4 w-4 ${modalStyles.jobEndDateCalloutIcon}`} aria-hidden="true" />
            <p className="m-0">
              Please set the job end date to move this current job into your previous
              employment history.
            </p>
          </div>

          <div className={modalStyles.jobEndDateField}>
            <label
              className={modalStyles.jobEndDateFieldLabel}
              htmlFor="popup_job_end_date"
              data-testid="application-income-tab-popup-job-end-date-label"
            >
              Job End Date<span className="text-red-500">*</span>
            </label>
            <div id="popup_job_end_date" className="w-full">
              <EditDatePicker
                value={value}
                onChange={onChange}
                allowPastDates={true}
                allowFutureDates={false}
                displayFormat="mdy"
                calendarPortalId="liberty-datepicker-job-end-date"
              />
            </div>
          </div>
        </div>

        <div className={modalStyles.jobEndDateModalFooter} role="group">
          <button
            type="button"
            data-testid="application-income-tab-on-cancel-button"
            onClick={onCancel}
            className={modalStyles.btnSecondary}
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="application-income-tab-on-save-button"
            onClick={onSave}
            disabled={!isValid}
            className={modalStyles.btnPrimary}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
